import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Table,
  Select,
  Tag,
  Popover,
  Input,
  Modal,
  message,
  Tooltip,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  LinkOutlined,
  MoreOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Container } from "./style";
import { createVisibleToColumn } from "./VisibleToCell";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { setPracticeData } from "../../../store/features/CreatePractice";
const { confirm } = Modal;

const LIST_PRACTICES = gql`
  query ListPractices(
    $args: PaginationArgs
    $filters: ListPracticeInput
    $sortBy: SortByArgs
  ) {
    listPractices(args: $args, filters: $filters, sortBy: $sortBy) {
      count
      limit
      offset
      total
      practices {
        _id
        about
        description
        hint
        imageUrl
        link
        name
        rate
        hasAccess
        isBookmarked
        status
        viewsCount
        visibility
        visibilityType
        visibilityGroupId
        completesCount
        startsCount
        wrap_up
        publishedAt
        scheduledAt
        expertise {
          _id
          title
          backgroundColor
          linearColor
          backgroundUrl
          description
        }
        expertises {
          _id
          title
          backgroundColor
          linearColor
          backgroundUrl
          description
        }
        createdBy {
          _id
          name
          email
          belief
          about
          experience
        }
      }
    }
  }
`;

const UPDATE_PRACTICE = gql`
  mutation updatePractice($updatePracticeInput: UpdatePracticeInput!) {
    updatePractice(payload: $updatePracticeInput) {
      _id
    }
  }
`;

const DELETE_PRACTICE = gql`
  mutation deletePractice($_id: String!) {
    deletePractice(_id: $_id) {
      _id
    }
  }
`;

export default function PracticeManager({ onDeleteSuccess, groups = [] }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState({});
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const initVariables = {
    filters: {
      createdBy: userInfo?.coach?._id || null,
      status: null,
    },
    args: {
      limit: 10,
      offset: 0,
    },
    sortBy: {
      createdAt: -1,
      publishedAt: -1,
      scheduledAt: -1,
    },
  };
  // eslint-disable-next-line no-unused-vars
  const [getPractices, { data, refetch }] = useLazyQuery(
    LIST_PRACTICES,
    {
      variables: initVariables,
    }
  );
  const [updatePractice] = useMutation(UPDATE_PRACTICE);
  const [deletePractice] = useMutation(DELETE_PRACTICE);

  const statusList = [
    {
      label: "Status: All",
      value: "all",
    },
    {
      label: "Status: Published",
      value: "PUBLISHED",
    },
    {
      label: "Status: Draft",
      value: "DRAFTED",
    },
    {
      label: "Status: Scheduled",
      value: "SCHEDULED",
    },
    {
      label: "Status: Hide",
      value: "HIDDEN",
    },
  ];

  function fixLink(link) {
    // 如果链接以 http:// 或 https:// 开头，则不需要做任何处理
    if (link.startsWith("http://") || link.startsWith("https://")) {
      return link;
    }
    // 否则，添加默认的前缀 http://
    return "http://" + link;
  }

  const columns = [
    {
      title: "Practice Name",
      dataIndex: "name",
      render: (text, row) => {
        if (row.link) {
          return (
            <a
              href={fixLink(row.link)}
              target="_blank"
              rel="noreferrer"
              className="c-blue"
            >
              <LinkOutlined className="mr-10" />
              {text}
            </a>
          );
        } else {
          return text;
        }
      },
    },
    createVisibleToColumn({ groups, navigate }),
    {
      title: "Category",
      dataIndex: "category",
      render: (text, row) => {
        let array = [];
        for (const item of row.expertises) {
          array.push(
            <Tag
              key={item.title}
              color={item.backgroundColor}
              className="mb-10"
            >
              {item.title}
            </Tag>
          );
        }
        return array;
      },
    },
    {
      title: "Completed",
      dataIndex: "completesCount",
    },
    {
      title: "Views",
      dataIndex: "viewsCount",
    },
    {
      title: "Publish Date",
      dataIndex: "publishedAt",
      // sorter: true,
      render: (text) => {
        if (!text) {
          return "";
        }
        const date = new Date(text);
        const options = {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          // second: "numeric",
        };
        const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
          date
        );
        return formattedDate;
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 80,
      render: (action, row) => {
        return (
          <Popover
            placement="right"
            content={
              <ul>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onEdit(row)}
                  >
                    Edit
                  </Button>
                </li>
                <li>
                  {row.status !== "HIDDEN" && (
                    <Button
                      type="text"
                      block
                      className="text-left"
                      onClick={() => onHide(row)}
                    >
                      Hide
                    </Button>
                  )}
                </li>
                <li>
                  {row.status === "HIDDEN" && (
                    <Button
                      type="text"
                      block
                      className="text-left"
                      onClick={() => onUnhide(row)}
                    >
                      Unhide
                    </Button>
                  )}
                </li>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onDelete(row)}
                  >
                    Delete
                  </Button>
                </li>
              </ul>
            }
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: "16px" }} />}
            ></Button>
          </Popover>
        );
      },
    },
  ];

  useEffect(() => {
    setTableParams(initVariables);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  useEffect(() => {
    if (data?.listPractices?.practices) {
      setList(data.listPractices.practices);
    } else {
      setList([]);
    }
    setTotal(data?.listPractices?.total);
  }, [data]);

  const getData = async () => {
    if (!tableParams.args || !tableParams.args.limit) {
      return;
    }
    setLoading(true);
    // if (called) {
    await refetch(tableParams);
    // } else {
    //   await getPractices(tableParams);
    // }
    setLoading(false);
  };
  const onSearch = (val) => {
    let param = {
      filters: Object.assign({}, tableParams.filters, {
        name: val.target.value,
      }),
      args: tableParams.args,
    };
    if (!val.target.value) {
      delete param.filters.name;
    }
    setTableParams(param);
  };
  const onSearchChange = (val) => {
    if (!val.target.value) {
      let param = { filters: tableParams.filters, args: tableParams.args };
      delete param.filters.name;
      setTableParams(param);
    }
  };
  const onStatusChange = (val) => {
    let param = { filters: tableParams.filters, args: tableParams.args };
    if (val === "all") {
      param.filters.status = null;
    } else if (val) {
      param.filters.status = val;
    }
    setTableParams(param);
  };

  const onTableChange = (pagination, filters, sorter) => {
    console.log(pagination);
    setTableParams({
      filters: tableParams.filters,
      args: {
        limit: pagination.pageSize,
        offset: pagination.pageSize * (pagination.current - 1),
      },
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.args?.limit) {
      setList([]);
    }
  };

  // Actions
  const onEdit = (data) => {
    dispatch(setPracticeData(data));
    navigate("/EditPractice");
  };
  const onHide = (data) => {
    confirm({
      title: `Hide the practice "${data.name}"?`,
      content: "Are you sure to hide this practice",
      okText: "Yes, hide this practice",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        const param = {
          _id: data._id,
          status: "HIDDEN",
        };
        await updatePractice({
          variables: {
            updatePracticeInput: param,
          },
        });
        message.success(`Practice "${data.name}" is hidden from the app!`);
        getData();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onUnhide = (data) => {
    confirm({
      title: `Unhide the practice "${data.name}"?`,
      content: "Are you sure to unhide this practice",
      okText: "Yes, unhide this practice",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        const param = {
          _id: data._id,
          status: "PUBLISHED",
        };
        await updatePractice({
          variables: {
            updatePracticeInput: param,
          },
        });
        message.success(
          `Practice "${data.name}" is unhidden adn available in the app!`
        );
        getData();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onDelete = (data) => {
    confirm({
      title: `Delete the practice "${data.name}"?`,
      content:
        "Deleting this practice will permanently remove it from your account. Are you sure to delete this practice?",
      okText: "Yes, delete this practice",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        await deletePractice({
          variables: {
            _id: data._id,
          },
        });
        message.success(`Practice "${data.name}" is deleted successfully!`);
        getData();
        onDeleteSuccess && onDeleteSuccess();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  return (
    <Container>
      <div className="flex-between">
        <Input
          size="large"
          placeholder="Search"
          bordered={false}
          prefix={
            <Tooltip title="Press enter to search">
              <SearchOutlined
                style={{ fontSize: "20px", marginRight: "10px" }}
              />
            </Tooltip>
          }
          className="table-search"
          onPressEnter={onSearch}
          onChange={(e) => onSearchChange(e)}
        />
        <div className="table-filter">
          <Select
            defaultValue="Status: All"
            style={{ width: 150 }}
            onChange={onStatusChange}
            options={statusList}
          />
          {/* <Select
            className="ml-10"
            defaultValue="Categories: All"
            style={{ width: 150 }}
            onChange={onStatusChange}
            options={statusList}
          /> */}
        </div>
      </div>
      <Table
        columns={columns}
        rowKey={(record) => record._id}
        dataSource={list}
        pagination={{ total }}
        loading={loading}
        onChange={onTableChange}
      />
    </Container>
  );
}
